import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProfileNudge } from '../hooks/useProfileNudge';
import { ProfileNudgeModal, ProfileCompletionBanner } from './ProfileNudge';

const API_URL = process.env.REACT_APP_API_URL;

const ProfileContext = createContext({
  profile: null,
  updateProfile: () => {},
  inlineNudgeNeeded: () => false,
  openNudgeForField: () => {},
});

export function useProfile() {
  return useContext(ProfileContext);
}

export function ProfileNudgeProvider({ children }) {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const location  = useLocation();
  const navigate  = useNavigate();
  const [profile, setProfile] = useState(null);

  const getToken = useCallback(() =>
    getAccessTokenSilently({ authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE } }),
  [getAccessTokenSilently]);

  // Fetch profile on mount once authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    getToken().then(token =>
      fetch(`${API_URL}api/profile/me`, { headers: { Authorization: `Bearer ${token}` } })
    ).then(r => r.json()).then(data => {
      setProfile(data.profile || {});
    }).catch(err => console.error('Profile fetch error:', err.message));
  }, [isAuthenticated]);

  const updateProfile = useCallback((updates) => {
    setProfile(prev => ({ ...prev, ...updates }));
  }, []);

  // Returns true if a specific field key is missing from profile
  const inlineNudgeNeeded = useCallback((fieldKey) => {
    if (!profile) return false;
    return !profile[fieldKey];
  }, [profile]);

  const {
    activeField, value, setValue, saving, dismiss, save,
    bannerDismissed, setBannerDismissed,
  } = useProfileNudge(profile, getToken, API_URL);

  const handleSave = () => save(updateProfile);

  // Don't show modal on /profile page — they're already there
  const isProfilePage = location.pathname === '/profile';

  return (
    <ProfileContext.Provider value={{ profile, updateProfile, inlineNudgeNeeded, openNudgeForField: () => {} }}>
      {children}

      {/* Modal — shown on any page except /profile */}
      {!isProfilePage && (
        <ProfileNudgeModal
          activeField={activeField}
          value={value}
          setValue={setValue}
          saving={saving}
          onSave={handleSave}
          onDismiss={dismiss}
        />
      )}
    </ProfileContext.Provider>
  );
}

// Separate component — drop into Dashboard
export function DashboardProfileBanner() {
  const { profile, updateProfile, openNudgeForField } = useProfile();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    const d = localStorage.getItem('raah_banner_dismissed');
    if (!d) return false;
    // Re-show banner after 7 days even if dismissed
    return Date.now() - parseInt(d) < 7 * 24 * 60 * 60 * 1000;
  });

  const handleDismiss = () => {
    localStorage.setItem('raah_banner_dismissed', Date.now().toString());
    setDismissed(true);
  };

  return (
    <ProfileCompletionBanner
      profile={profile}
      dismissed={dismissed}
      onDismiss={handleDismiss}
      onComplete={() => navigate('/profile')}
    />
  );
}